import React, { useState, useEffect } from 'react';
import { Button, FileInput, Table, TextBox } from '@ds/ui';
import { useConfig } from '../../context/ConfigContext';
import { BannerInfo } from '../../types/BannerInfo';
import { apiFetch, handleApiError } from '../../utils/apiClient';
interface HSMKey {
  label: string;
  checksum: string;
  startDate: string;
  usedBy: string | null;
  keyType: number;
  keyObject: number;
  publicKey: string | null;
}

interface HSMKeyPackageResponse {
  hsmKeyPackage: {
    hsmKeys: HSMKey[];
    cumulativeChecksum: string;
    hsmKeyChecksums: { [key: string]: string };
    error: string | null;
  }
}

interface HSMKeysProps {
  onBannerUpdate: (bannerInfo: BannerInfo | null) => void
}

const HSMKeys: React.FC<HSMKeysProps> = ({ onBannerUpdate }) => {
  const [keyPackage, setKeyPackage] = useState<HSMKeyPackageResponse['hsmKeyPackage'] | null>(null);
  const [showAllKeys, setShowAllKeys] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hsmImportLabel, setHsmImportLabel] = useState('');
  const [createKeyLabel, setCreateKeyLabel] = useState<string>('');
  const [importKeyLabel, setImportKeyLabel] = useState<string>('');
  const [showImportForm, setShowImportForm] = useState<boolean>(false);
  const [hsmImportFile, setHsmImportFile] = useState<File | null>(null);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [originalValue, setOriginalValue] = useState("New Key Name");

  const { internalSiteEnabled } = useConfig();

  const fetchHSMKeys = async () => {
    try {
      const response = await apiFetch('Admin/Home/GetHSMKeyPackage', {
        method: 'GET'
      });
      if (!response.ok) {
            await handleApiError(response);
      }
      const data: HSMKeyPackageResponse = await response.json();
      setKeyPackage(data.hsmKeyPackage);
    } catch (err) {
      setError('Error fetching HSM key data');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHSMKeys();
  }, []);

  const handleCreateKey = async () => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('deriver', 'HSM');
      formData.append('keylabel', createKeyLabel);
      
      const response = await apiFetch('Admin/Home/HSMKeyAdd', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        await handleApiError(response);
      }
      
      // Add a small delay before fetching updated data, otherwise the new key will not be shown
      setTimeout(() => {
        fetchHSMKeys();
      }, 1000); // 1 second delay
      
      // Clear the input field after successful creation
      setCreateKeyLabel('');
      onBannerUpdate({ type: 'success', message: 'HSM key created successfully' });
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 5000); // Reset after 5 seconds
    } catch (err) {
      console.error(err);
      onBannerUpdate({ type: 'danger', message: 'Error creating HSM key: ' + err });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setHsmImportFile(file);
      onBannerUpdate({ 
        type: 'success', 
        message: `Selected file: ${file.name}` 
      });
    }
  };

  interface BannerInfo {
    type: 'success' | 'danger'
    message: string
    retryAction?: () => void
  }

  const handleUploadHSMKey = async () => {
    if (!hsmImportFile) {
      onBannerUpdate({ type: 'danger', message: 'No file selected' });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('hsmImportFile', hsmImportFile);
      formData.append('hsmImportLabel', hsmImportLabel);

      const response = await apiFetch('Admin/Home/HSMKeyImport', {
        method: 'POST',
        body: formData 
      });

      if (!response.ok) {
        await handleApiError(response);
      }

      // Handle successful upload
      setTimeout(() => {
        fetchHSMKeys();
      }, 1000);
      setHsmImportFile(null);
      setHsmImportLabel('');
      onBannerUpdate({ type: 'success', message: 'HSM key uploaded successfully' });
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Error uploading HSM key');
      onBannerUpdate({ type: 'danger', message: err instanceof Error ? err.message : 'Error uploading HSM key' });
    }
  };

  const toggleImportForm = () => {
    setShowImportForm(!showImportForm);
  };

  if (isLoading) return <div style={{ paddingLeft: '1rem' }}>Loading HSM key data...</div>;
  if (error) return <div style={{ paddingLeft: '1rem' }}>Error: {error}</div>;
  if (!keyPackage) return <div style={{ paddingLeft: '1rem' }}>No HSM key data available</div>;

  const toggleShowAllKeys = () => setShowAllKeys(!showAllKeys);

  const handleFocus = () => {
    setIsEditing(true);
    setOriginalValue(inputValue);
  };

  const handleBlur = () => {
    if (inputValue === originalValue) {
      setIsEditing(false);
    }
  };

  return (
    <div>
      <Table columnSettings={[
        { alignment: 'left', width: '10rem' },
        { alignment: 'left', width: '1.25rem' },
        { alignment: 'left', width: '3.125rem' },
        { alignment: 'left', width: '6.25rem' }
      ]}>
        <Table.Body>
          <Table.Row>
            <Table.Cell _style={{ whiteSpace: 'normal', verticalAlign: 'top' , paddingTop: 0, paddingLeft: 16}}>
              <span >{keyPackage.hsmKeys.length}</span>
            </Table.Cell>
            <Table.Cell _style={{ whiteSpace: 'normal', verticalAlign: 'top', paddingTop: 0 }} colSpan={4}>(Checksum: {keyPackage.cumulativeChecksum})</Table.Cell>
          </Table.Row>
          <Table.Row>     
            <Table.Cell _style={{ whiteSpace: 'normal', verticalAlign: 'top' }} colSpan={4}>
              <div >
                <TextBox
                  width="17.5rem"
                  label="Create New Key"
                  type="text"
                  placeholder="New Key Name"
                  value={createKeyLabel}
                  onChange={(e) => setCreateKeyLabel(e.target.value)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  endElement={<Button 
                    onClick={handleCreateKey} 
                    disabled={isLoading}
                    accessibilityText="Create New Key" 
                    icon={isLoading ? "processSpinner" : (isSuccess ? "check" : (isEditing ? "arrowRight" : "pencil"))}
                    kind={isSuccess ? "primary" : "tertiary"}
                    size="small"/>}
              />
            </div>
          </Table.Cell>
        </Table.Row>
        {/* Internal use only */}
        {internalSiteEnabled && (
          <>
            <Table.Row>
                <Table.Cell _style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }} colSpan={4}>
                  <Button 
                    kind="secondary" 
                    text="Import an Existing Key for Wrapping" 
                    onClick={toggleImportForm}
                  />
                </Table.Cell>
              </Table.Row>
              
              {showImportForm && (
                <>
                  <Table.Row>
                    <Table.Cell>
                      Key Label 
                    </Table.Cell>
                    <Table.Cell>
                      <TextBox
                        label=""
                        type="text"
                        value={hsmImportLabel}
                        onChange={(e) => setHsmImportLabel(e.target.value)}
                        width="13rem"
                      />
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>
                      PEM File  
                    </Table.Cell>
                    <Table.Cell _style={{ width: '13rem' }}>
                      <FileInput
                        accept=".pem"
                        onChange={handleFileChange}
                        text="Upload"
                        kind="primary"
                      />
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell>
                      <Button
                        disabled={!hsmImportFile} 
                        onClick={handleUploadHSMKey}
                        text="Submit"
                        kind="primary"
                      />
                    </Table.Cell>
                  </Table.Row>
                </>
              )}
            </>
          )}
          {/* End of Internal use only */}
          <Table.Row>
            <Table.Cell _style={{ whiteSpace: 'nowrap', verticalAlign: 'top' }} colSpan={2}>
              <Button 
                kind="secondary" 
                text={showAllKeys ? "Hide All Keys" : "Show All Keys"} 
                onClick={toggleShowAllKeys}
              />
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>

      {showAllKeys && keyPackage && (
        <Table columnSettings={[
          { alignment: 'left', width: '12.5rem' },
          { alignment: 'left', width: '15rem' },
          { alignment: 'left', width: '30rem' }
        ]}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell text="Label" />
              <Table.HeaderCell text="Date Created" />
              <Table.HeaderCell text="Checksum" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {keyPackage.hsmKeys.map((key, index) => (
              <Table.Row key={index}>
                <Table.Cell>{key.label}</Table.Cell>
                <Table.Cell>{key.startDate}</Table.Cell>
                <Table.Cell _style={{ minWidth: '30rem', wordBreak: 'break-all' }}>{key.checksum}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </div>
  );
};

export default HSMKeys;
